import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { cn } from '../../lib/utils';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  toolbarClassName?: string;
}

const exec = (command: string, value?: string) => {
  try {
    document.execCommand(command, false, value);
  } catch {
    // Ignore unsupported execCommand errors.
  }
};

export type RichTextEditorHandle = {
  focus: () => void;
  insertToken: (token: string) => void;
};

export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  ({ value, onChange, placeholder, className, toolbarClassName }, ref) => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!contentRef.current) return;
    if (contentRef.current.innerHTML !== value) {
      contentRef.current.innerHTML = value || '';
    }
  }, [value]);

  useEffect(() => {
    // Make Enter insert <p> blocks instead of the browser default <div>, so
    // paragraph breaks survive the website's HTML sanitizer (which only keeps
    // an allow-list of tags). Best effort — unsupported browsers just ignore it.
    exec('defaultParagraphSeparator', 'p');
  }, []);

  const handleInput = () => {
    if (!contentRef.current) return;
    onChange(contentRef.current.innerHTML);
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      try {
        contentRef.current?.focus();
      } catch {
        // ignore
      }
    },
    insertToken: (token: string) => {
      const t = String(token || '');
      if (!t) return;
      // Best effort: insert at cursor if editor is focused; otherwise append.
      try {
        contentRef.current?.focus();
        exec('insertText', t);
        // If insertText isn't supported, fallback to append.
        if (contentRef.current && !contentRef.current.innerHTML.includes(t)) {
          contentRef.current.innerHTML = (contentRef.current.innerHTML || '') + t;
        }
        onChange(contentRef.current?.innerHTML || '');
      } catch {
        if (!contentRef.current) return;
        contentRef.current.innerHTML = (contentRef.current.innerHTML || '') + t;
        onChange(contentRef.current.innerHTML);
      }
    },
  }));

  return (
    <div className={cn('border rounded-md', className)}>
      <div className={cn('flex flex-wrap gap-1 border-b p-1 bg-muted/30', toolbarClassName)}>
        <button type="button" className="px-2 py-1 text-sm hover:bg-muted rounded" onClick={() => exec('bold')} aria-label="Fett">B</button>
        <button type="button" className="px-2 py-1 text-sm italic hover:bg-muted rounded" onClick={() => exec('italic')} aria-label="Kursiv">I</button>
        <button type="button" className="px-2 py-1 text-sm underline hover:bg-muted rounded" onClick={() => exec('underline')} aria-label="Unterstrichen">U</button>
        <button type="button" className="px-2 py-1 text-sm hover:bg-muted rounded" onClick={() => exec('insertUnorderedList')} aria-label="Liste">• List</button>
        <button type="button" className="px-2 py-1 text-sm hover:bg-muted rounded" onClick={() => exec('insertOrderedList')} aria-label="Nummeriert">1. List</button>
        <button
          type="button"
          className="px-2 py-1 text-sm hover:bg-muted rounded"
          onClick={() => {
            const url = window.prompt('Link-URL eingeben:', 'https://');
            if (url) exec('createLink', url);
          }}
          aria-label="Link"
        >Link</button>
        <button type="button" className="px-2 py-1 text-sm hover:bg-muted rounded" onClick={() => exec('removeFormat')} aria-label="Format entfernen">Clear</button>
      </div>
      <div
        ref={contentRef}
        className="min-h-[180px] max-h-[520px] overflow-y-auto overflow-x-hidden p-3 text-sm outline-none break-words [&_*]:max-w-full [&_img]:h-auto [&_img]:max-w-full [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2"
        contentEditable
        role="textbox"
        aria-multiline
        data-placeholder={placeholder || ''}
        onInput={handleInput}
        suppressContentEditableWarning
      />
    </div>
  );
});

export default RichTextEditor;


